import styled, { css, keyframes } from 'styled-components';
import { FADE, t } from 'theme';
import { HealthBands } from 'model/party/resources/health';

const pulse = keyframes`
  0% { transform: scale(1); font-weight: 100; }
  50% { transform: scale(1.5); font-weight: 900; }
  100% { transform: scale(1); font-weight: 100; }
`;

/*
 * The last band had a slow opacity gutter on this readout as well, and it is gone on purpose.
 * Two things were wrong with it. It was redundant — the closing vignette is already the moving
 * part, and it catches the eye of a player looking at the log or the map far better than a
 * header can. And it fought the one job the readout has: at the bottom of the cycle the number
 * was sitting at half opacity, over the darkest part of the vignette, at the exact moment the
 * player most needs to read it. Weight is enough here. Keep the motion in one place.
 */

const ResourceList = styled.ul`
  display: flex;
  justify-content: flex-start;
  margin: 0;
  align-items: center;
`;

const ResourceTooltip = styled.span`
  position: absolute;
  width: 150px;
  color: ${t('fg')};
  background: ${t('panel')};
  transition: color ${FADE}, background-color ${FADE};
  font-weight: 100;
`;

const CalendarIcon = styled.span`
  filter: grayscale(1);
  padding-left: 0.1rem;
  font-size: 20px;
  width: 20px;
`;

/**
 * The row inverts rather than gaining anything.
 *
 * Health in danger is not marked with a colour, an icon or a second element — there is no room in
 * this interface for any of those, and a hue used once to mean *bad* would be the first decorative
 * colour in the game. It is marked by taking the weight out of everything else and putting it
 * here, which the eye reads as fast as red and costs the page nothing.
 *
 * FAILING is only that: the number gets heavy and opens up. DYING opens it further and drops the
 * rest of the row to `muted`, so the header stops offering the player anything to read but this.
 */
const bandStyles = props => {
  switch (props.$band) {
    case HealthBands.DYING:
      return css`
        font-weight: 700;
        letter-spacing: 0.08em;
      `;
    case HealthBands.FAILING:
      return css`
        font-weight: 700;
        letter-spacing: 0.04em;
      `;
    default:
      return null;
  }
};

const ResourceListItem = styled.li`
  display: inline-flex;
  align-items: center;
  padding: 0 10px;
  border-right: 1px dotted ${t('rule')};
  font-family: monospace;
  transition: border-color ${FADE}, color ${FADE};
  & ${ResourceTooltip} {
    display: none;
  }
  &:hover ${ResourceTooltip} {
    display: block;
  }
  animation: ${props =>
    props.animate
      ? css`
          ${pulse} 3s
        `
      : 'none'};

  &:last-child {
    border-right: 0;
    > span {
      margin-left: 5px;
    }
  }

  /* Everything the traveler is not about to die of, while he is about to die of something. */
  ${props =>
    props.$dimmed &&
    css`
      color: ${t('muted')};
    `}

  ${bandStyles}
`;

export { CalendarIcon, ResourceList, ResourceListItem, ResourceTooltip };
